/* eslint-disable camelcase */
import * as logger from 'universal-logger';
import { all, call, put, select } from 'redux-saga/effects';
import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { cancelFlowStep, cancelFlowUserAttributes, userProfile } from 'redux/saga/entities/bound';
import { querySelector } from 'redux/saga/selectors/request';
import { apiErrorMessages } from 'components/Errors/Error.messages';
import { ec } from 'helpers/multitrack';
import { segment } from 'helpers/segment';
import { addFailureNotification } from 'features/Banners/Banners.reducer';

export const COHORT = {
  control: 'control',
  excluded: 'excluded',
  test: 'test',
};

export const STEP_IDS = {
  churnSurvey: 'churn_survey',
  manageMyMembership: 'manage_my_membership',
  planSwitching: 'plan_switching',
};

export const PAGE_TYPES = {
  cancelHold: 'cancel_hold',
  planSwitching: 'plan_switching',
};

export const ACTIONS = {
  confirmHold: 'confirm_hold',
  confirmCancel: 'confirm_cancel',
  confirmSaveOffer: 'confirm_save_offer',
  dismiss: 'dismiss',
  select: 'select',
};

export const SOURCES = {
  planSwitching: 'plan_switching',
  manageMyMembership: 'manage_my_membership',
};

export const EXPERIMENT_PREFIX = 'cancel_flow_test_3_';

export const objectToQueryString = obj =>
  Object.keys(obj)
    .map(key => `${key}=${obj[key]}`)
    .join('&');

export const formatProfitabilityGroup = profitabilityGroup =>
  profitabilityGroup &&
  profitabilityGroup
    .trim(' ')
    .split(' ')
    .join('_')
    .toLowerCase();

export function* getAttributes(client) {
  const { id } = yield call(fetchEntityIfNeeded, userProfile, [client]);
  const attributes = yield call(fetchEntityIfNeeded, cancelFlowUserAttributes, [client, id]);

  attributes.profitability_group = formatProfitabilityGroup(attributes.profitability_group);

  return attributes;
}

export function* sendAnalytics(client, eventName, additionalProps) {
  try {
    const profile = yield call(fetchEntityIfNeeded, userProfile, [client]);
    const [attributes, { cohort, source }, cancelStep] = yield all([
      call(getAttributes, client),
      select(querySelector),
      select(cancelFlowStep.data, additionalProps.step),
    ]);

    const props = {
      cohort,
      current_plan_id: profile.membership?.plan_id,
      current_plan_code: profile.membership?.plan_name,
      msa_id: profile.msa?.id,
      source,
      user_id: profile.id,
      ...attributes,
      ...additionalProps,
      ...(cancelStep?.analyticsProps ?? {}),
    };

    yield all([call(segment.track, eventName, props), call(ec, eventName, props)]);
  } catch (err) {
    // we should allow analytics tracking to fail gracefully
  }
}

export function* trackOptimizelyStart(client) {
  const attributes = yield call(getAttributes, client);
  yield call(sendAnalytics, client, 'Optimizely Experiment Start', {
    experiment_id: `${EXPERIMENT_PREFIX}${attributes.profitability_group}`,
  });
}
export function* startMembershipAnalytics(client, additionalProps = {}) {
  yield call(sendAnalytics, client, 'Start Manage Membership', additionalProps);
}

export function* stepViewedAnalytics(client, additionalProps = {}) {
  const { churn_reason, previous } = yield select(querySelector);
  yield call(sendAnalytics, client, 'Manage Membership Step Viewed', {
    ...additionalProps,
    churn_reason,
    previous,
  });
}

export function* completedMembershipStepAnalytics(client, additionalProps) {
  const { churn_reason, previous } = yield select(querySelector);
  yield call(sendAnalytics, client, 'Manage Membership Step Completed', {
    ...additionalProps,
    churn_reason,
    previous,
  });
}

export function* completedManageMembershipAnalytics(client, additionalProps) {
  const { previous } = yield select(querySelector);
  yield call(sendAnalytics, client, 'Complete Manage Membership', {
    ...additionalProps,
    previous,
  });
}

export function* handleCancelFlowFailure(client, err, additionalProps = {}, shouldShowErrorBanner = true) {
  try {
    const errorMessage = err?.message;
    const errorTitle = err?.name;

    yield all([
      call(sendAnalytics, client, 'Manage Membership Step Failed', {
        ...additionalProps,
        error: errorMessage,
        error_title: errorTitle,
      }),
      call(logger.error, 'An error occurred in the save flow', {
        error: err,
        ...additionalProps,
      }),
    ]);
    if (shouldShowErrorBanner) {
      yield put(addFailureNotification(apiErrorMessages.genericMessage));
    }
  } catch {
    // if something goes wrong while sending the error analytics the app shouldn't crash
  }
}
